import React, { useEffect, useRef, useState } from 'react';
import { useAttachedTrack } from '../hooks/useAttachedTrack';
import { getOptimalTileSize } from '../utils/tileGrid';
import { IoChevronDown, IoMic, IoMicOff } from 'react-icons/io5';
import logo from '../images/icon_small.png';
import { imageSrc } from '../utils/avatar';
import { useLanguage } from '../i18n/LanguageContext';

const avatarUrl = (imageUrl) => imageSrc(imageUrl, { size: 128 });

// Attaches/detaches the raw LiveKit track against this tile's own <video>
// element - the hook (useVoiceConnection) deliberately doesn't do this
// itself, since adaptiveStream only keeps a subscription alive while its
// track is attached to a real, sized, on-screen element, which only the
// component actually rendering it can provide.
// Named export (alongside the default below) so VideoWatchOverlay can reuse
// the exact same tile - live feed vs. avatar+speaking-ring fallback, mic
// icon - for its small camera strip instead of duplicating that logic.
export function VideoTile({ participant, track, occupant }) {
  // Turning a camera off calls LiveKit's track.mute(), not unpublish - the
  // publication (and this tile's `track` prop) stays around the whole time,
  // it just stops sending/receiving frames, which renders as a frozen/black
  // <video> rather than actually going away. participant.cameraEnabled
  // (Participant.isCameraEnabled) is the one source of truth that already
  // accounts for that muted state, so it - not just track's presence - has
  // to gate whether the video element or the avatar fallback shows.
  const showVideoEl = track && participant.cameraEnabled;
  const videoRef = useAttachedTrack(track, showVideoEl);

  const avatarSrc = occupant && occupant.imageUrl ? avatarUrl(occupant.imageUrl) : logo;
  const tileClass = `VideoTile ${participant.speaking ? 'VideoTileSpeaking' : ''} ${participant.isLocal ? 'VideoTileLocal' : ''}`;

  return (
    <div className={tileClass}>
      {showVideoEl ? (
        // muted is mandatory here, including for remote tiles - audio
        // already plays through the separate hidden <audio>/gain-node path
        // in useVoiceConnection (that's what makes the 0-300% volume slider
        // work); an unmuted <video> would double the audio and bypass it.
        <video ref={videoRef} className="VideoTileVideo" autoPlay playsInline muted />
      ) : (
        <div className="VideoTileOff">
          <div className={`VideoTileAvatarWrap ${participant.speaking ? 'VideoTileAvatarSpeaking' : ''}`}>
            <img className="VideoTileAvatar" src={avatarSrc} alt={participant.name || participant.identity} />
          </div>
        </div>
      )}
      <div className="VideoTileName">
        <span>{participant.name || participant.identity}</span>
        {participant.muted ? <IoMicOff className="VideoTileMic VideoTileMicMuted" /> : <IoMic className="VideoTileMic" />}
      </div>
    </div>
  );
}

// Rendered as a sibling of .VoicePanel (same reasoning as MusicSearchModal
// there) - .VoicePanel is position:fixed, so nesting anything meant to be
// independently positioned inside it doesn't work. Bot identities are
// excluded from the grid - the music bot has no camera to show, matching
// the same 'bot:' prefix exclusion the join/leave sounds already use.
export default function VideoGridOverlay({ roomLabel, participants, videoTracks, occupants, onClose }) {
  const { t } = useLanguage();
  const tiles = participants.filter((p) => !p.identity.startsWith('bot:'));

  const gridRef = useRef(null);
  const [gridSize, setGridSize] = useState(null);

  // Re-measured on every resize (window resize, sidebar toggle, etc.) so the
  // column count stays optimal instead of being frozen at whatever size the
  // overlay happened to open at.
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(([entry]) => {
      setGridSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const optimalTile = gridSize ? getOptimalTileSize(tiles.length, gridSize.width, gridSize.height) : null;

  return (
    <div className="VideoOverlay glass">
      <div className="VideoOverlayHeader">
        <span>{roomLabel}</span>
        <button onClick={onClose} className="VideoOverlayCloseBtn" title={t('voice.hideVideo')}><IoChevronDown /></button>
      </div>
      <div
        className="VideoGrid"
        ref={gridRef}
        style={optimalTile ? { gridTemplateColumns: `repeat(${optimalTile.cols}, ${optimalTile.tileWidth}px)` } : undefined}
      >
        {tiles.map((p) => (
          <VideoTile
            key={p.identity}
            participant={p}
            track={videoTracks[p.identity]}
            occupant={occupants.find((o) => o.userId === p.identity)}
          />
        ))}
      </div>
    </div>
  );
}
